/**
 * Filtros de módulo — chips premium estilo Linear/Attio.
 * Ícone + label + contador em badge; scroll horizontal sem scrollbar.
 *
 * @version 3.0.0 - 2026-04-20 — refinamento "Enterprise Premium"
 */
import {
  Inbox, MessagesSquare, AtSign, CalendarClock, Zap,
  FileSignature, Wallet, ListChecks, Briefcase, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NotificationModule } from '@/hooks/useNotificationCenter';

interface Props {
  module: NotificationModule;
  onChange: (m: NotificationModule) => void;
  counts: Record<NotificationModule, number>;
}

const MODULES: { id: NotificationModule; label: string; icon: LucideIcon }[] = [
  { id: 'all',           label: 'Todos',        icon: Inbox          },
  { id: 'conversations', label: 'Conversas',    icon: MessagesSquare },
  { id: 'mentions',      label: 'Menções',      icon: AtSign         },
  // v1.1.0: nova aba dedicada para notificações de atividades (cadência atrasada)
  { id: 'activities',    label: 'Atividades',   icon: Zap            },
  { id: 'scheduling',    label: 'Agendamentos', icon: CalendarClock  },
  { id: 'crm',           label: 'CRM',          icon: Briefcase      },
  { id: 'contracts',     label: 'Contratos',    icon: FileSignature  },
  { id: 'financial',     label: 'Financeiro',   icon: Wallet         },
  { id: 'tasks',         label: 'Tarefas',      icon: ListChecks     },
];

export function NotificationModuleTabs({ module, onChange, counts }: Props) {
  return (
    <nav
      className="flex items-center gap-1.5 overflow-x-auto scrollbar-none -mx-1 px-1"
      style={{ scrollbarWidth: 'none' }}
      aria-label="Filtrar por módulo"
    >
      {MODULES.map(({ id, label, icon: Icon }) => {
        const count = counts[id] ?? 0;
        const active = module === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={cn(
              'group inline-flex items-center gap-1 h-6 px-2 rounded-full',
              'text-[11px] font-medium whitespace-nowrap',
              'border transition-all duration-150',
              active
                ? 'bg-foreground text-background border-foreground shadow-sm'
                : 'bg-card text-muted-foreground border-border hover:text-foreground hover:border-foreground/30 hover:bg-accent/40',
            )}
          >
            <Icon className="h-3 w-3" strokeWidth={1.75} />
            <span>{label}</span>
            {count > 0 && (
              <span
                className={cn(
                  'inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full',
                  'text-[10px] font-medium tabular-nums leading-none',
                  active
                    ? 'bg-background/20 text-background'
                    : 'bg-muted text-foreground/70 group-hover:bg-foreground/10',
                )}
              >
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
